import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, Download, ShieldCheck, Clock, AlertTriangle, Lock } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedDoc() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [input, setInput] = useState("");

  const fetchMeta = useCallback((c = "") => {
    axios.get(`${API}/public/document/${token}`, { params: c ? { code: c } : {} })
      .then((r) => { setMeta(r.data); if (c) setCode(c); })
      .catch(() => setError("This link is invalid or has expired."));
  }, [token]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const dl = (pdf) => `${API}/public/document/${token}/file?${pdf ? "as_=pdf&" : ""}${code ? `code=${encodeURIComponent(code)}` : ""}`;
  const expText = meta?.expires_at ? new Date(meta.expires_at).toLocaleString("en-GB") : "";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="shared-doc-page">
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16">
        <Logo className="mb-8 h-10" />
        {error ? (
          <div className="w-full rounded-2xl border border-rose-500/30 bg-rose-500/10 p-8 text-center" data-testid="shared-doc-error">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-rose-400" />
            <p className="text-slate-200">{error}</p>
            <p className="mt-2 text-sm text-slate-400">Ask the sender to share a fresh link.</p>
          </div>
        ) : !meta ? (
          <div className="h-48 w-full animate-pulse rounded-2xl bg-white/5" />
        ) : meta.locked ? (
          <div className="w-full rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="shared-doc-locked">
            <Lock className="mx-auto mb-3 h-8 w-8 text-amber-300" />
            <p className="mb-4 text-slate-200">This document is passcode protected.</p>
            <div className="flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Enter passcode" data-testid="shared-passcode-input"
                className="flex-1 rounded-lg border border-white/15 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-400" />
              <button onClick={() => fetchMeta(input)} data-testid="shared-passcode-submit" className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900">Unlock</button>
            </div>
          </div>
        ) : (
          <div className="w-full rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/20 text-blue-300"><FileText className="h-6 w-6" /></span>
              <div>
                <h1 className="text-xl font-bold">{meta.title}</h1>
                <p className="text-sm text-slate-400">{meta.category} · {meta.file_name}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-emerald-300"><ShieldCheck className="h-3.5 w-3.5" /> Shared securely via Suffle Online</div>
            {expText && <div className="mt-1 flex items-center gap-2 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> Link valid until {expText}</div>}
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={dl(false)} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-100" data-testid="shared-download-original">
                <Download className="h-4 w-4" /> Download original
              </a>
              <a href={dl(true)} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10" data-testid="shared-download-pdf">
                <FileText className="h-4 w-4" /> As PDF
              </a>
            </div>
          </div>
        )}
        <p className="mt-8 text-xs text-slate-500">Questions? Contact {meta?.contact || "support@suffleonline.com"}</p>
      </div>
    </div>
  );
}
